import React, { useEffect, useState } from 'react';
import { feeStatsService } from '../../services/api';
import { formatFcfa } from '../../utils/feeUtils';
import { useAcademicYear } from '../../contexts/AcademicYearContext';

const FeeStatsPage = () => {
  const { selectedYear } = useAcademicYear();
  const [stats, setStats] = useState(null);
  const [byClass, setByClass] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const params = selectedYear ? { annee_scolaire_id: selectedYear.id } : {};
      const [dashRes, classRes] = await Promise.all([
        feeStatsService.getDashboard(params),
        feeStatsService.getByClass(params),
      ]);
      setStats(dashRes.data.data);
      setByClass(classRes.data.data || []);
      setLoading(false);
    };
    fetch();
  }, [selectedYear]);

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto" />
      </div>
    );
  }

  if (!stats) return null;

  const agingLabels = {
    '0_30': '0–30 jours', '31_60': '31–60 jours', '61_90': '61–90 jours', 'over_90': '> 90 jours',
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 mb-6">Statistiques — Frais de scolarité</h1>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Taux de recouvrement</p>
          <p className="text-3xl font-bold text-primary-600">{stats.recovery_rate}%</p>
          <p className="text-xs text-gray-500 mt-1">{formatFcfa(stats.total_paid)} / {formatFcfa(stats.total_due)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Encours total</p>
          <p className="text-3xl font-bold text-gray-900">{formatFcfa(stats.outstanding)}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Impayés échus</p>
          <p className="text-3xl font-bold text-red-600">{formatFcfa(stats.overdue_amount)}</p>
          <p className="text-xs text-gray-500 mt-1">{stats.students_overdue} élève(s) ({stats.overdue_percent}%)</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600">Encaissements du mois</p>
          <p className="text-3xl font-bold text-green-600">{formatFcfa(stats.monthly_collected)}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Balance âgée */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Balance âgée (impayés)</h2>
          {Object.entries(stats.aging || {}).map(([key, amount]) => (
            <div key={key} className="flex justify-between items-center py-2 border-b last:border-0">
              <span className="text-sm text-gray-600">{agingLabels[key]}</span>
              <span className="font-medium">{formatFcfa(amount)}</span>
            </div>
          ))}
        </div>

        {/* Par rubrique */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Recouvrement par rubrique</h2>
          {stats.by_category?.map((cat) => {
            const rate = cat.total_due > 0 ? Math.round((cat.total_paid / cat.total_due) * 100) : 0;
            return (
              <div key={cat.code} className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span>{cat.name}</span>
                  <span>{rate}% — {formatFcfa(cat.total_paid)} / {formatFcfa(cat.total_due)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-primary-600 h-2 rounded-full" style={{ width: `${rate}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top débiteurs */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-medium mb-4">Top 10 débiteurs</h2>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b">
              <th className="text-left py-2">Élève</th>
              <th className="text-left py-2">Classe</th>
              <th className="text-right py-2">Montant impayé</th>
            </tr>
          </thead>
          <tbody>
            {stats.top_debtors?.map((d) => (
              <tr key={d.id} className="border-b">
                <td className="py-2">{d.nom} {d.prenom}</td>
                <td className="py-2 text-gray-500">{d.classe}</td>
                <td className="py-2 text-right text-red-600 font-medium">{formatFcfa(d.overdue_amount)}</td>
              </tr>
            ))}
            {(!stats.top_debtors || stats.top_debtors.length === 0) && (
              <tr><td colSpan="3" className="py-4 text-center text-gray-500">Aucun débiteur</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Par classe */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-lg font-medium mb-4">Recouvrement par classe</h2>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-gray-500 border-b">
              <th className="text-left py-2">Classe</th>
              <th className="text-right py-2">Élèves</th>
              <th className="text-right py-2">Total dû</th>
              <th className="text-right py-2">Total payé</th>
              <th className="text-right py-2">Impayés</th>
            </tr>
          </thead>
          <tbody>
            {byClass.map((c) => (
              <tr key={c.ouverture_classe_id} className="border-b">
                <td className="py-2 font-medium">{c.classe}</td>
                <td className="py-2 text-right">{c.student_count}</td>
                <td className="py-2 text-right">{formatFcfa(c.total_due)}</td>
                <td className="py-2 text-right text-green-600">{formatFcfa(c.total_paid)}</td>
                <td className="py-2 text-right text-red-600">{formatFcfa(c.overdue_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modes de paiement */}
      {stats.by_payment_method?.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-medium mb-4">Répartition par mode de paiement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.by_payment_method.map((m) => (
              <div key={m.type_reglement} className="text-center p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">{m.type_reglement || 'Non défini'}</p>
                <p className="text-lg font-semibold">{formatFcfa(m.amount)}</p>
                <p className="text-xs text-gray-500">{m.count} paiement(s)</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeStatsPage;
